import React, { useEffect, useRef, useState, useMemo } from 'react';
import { ArrowDown, MessageSquare, AlertCircle, CheckCircle2 } from 'lucide-react';
import { MessageBubble } from './MessageBubble';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { useGetApiWorkspacesWorkspaceIdConversationsConversationIdMessages } from '@/api/generated/conversations/conversations';
import { MOCK_CONVERSATION_ID, MOCK_MESSAGES } from '../mockData';
import type { MessageItem } from '@/types';
import { cn, extractArrayData, formatDayDivider } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface MessageListProps {
  conversationId: string;
  customerName?: string | null;
  isClosed?: boolean;
  closedAtUtc?: string | null;
  className?: string;
}

export const MessageList: React.FC<MessageListProps> = ({
  conversationId,
  customerName,
  isClosed,
  closedAtUtc,
  className,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const isUserScrollingRef = useRef(false);
  const isMock = conversationId === MOCK_CONVERSATION_ID;
  const { activeWorkspaceId } = useWorkspaceStore();

  const {
    data: messagesData,
    isLoading: isQueryLoading,
    isError: isQueryError,
    refetch,
  } = useGetApiWorkspacesWorkspaceIdConversationsConversationIdMessages<MessageItem[]>(
    activeWorkspaceId!,
    conversationId,
    undefined,
    {
      query: {
        enabled: Boolean(conversationId) && !isMock && !!activeWorkspaceId,
      },
    }
  );

  const isLoading = isMock ? false : isQueryLoading;
  const isError = isMock ? false : isQueryError;

  const rawMessages: MessageItem[] = isMock
    ? MOCK_MESSAGES
    : extractArrayData<MessageItem>(messagesData);

  // Sort messages chronologically (oldest at top, newest at bottom)
  const messages = useMemo(() => {
    return [...rawMessages].sort((a, b) => {
      // Pending messages always go to the bottom to prevent them from jumping around
      // if the client's clock is slightly behind the server's clock.
      if (a.isPending && !b.isPending) return 1;
      if (!a.isPending && b.isPending) return -1;

      const timeA = new Date(
        a.createdAtUtc ||
        a.createdAt ||
        (a as any).created_at ||
        a.timestamp ||
        0
      ).getTime();
      const timeB = new Date(
        b.createdAtUtc ||
        b.createdAt ||
        (b as any).created_at ||
        b.timestamp ||
        0
      ).getTime();

      if (!isNaN(timeA) && !isNaN(timeB) && timeA !== timeB) {
        return timeA - timeB;
      }
      return 0;
    });
  }, [rawMessages]);

  // Filter out internal AI tool calls, tool results, and empty messages
  const visibleMessages = useMemo(() => {
    return messages.filter((m) => {
      const sender = String(m.senderType).toUpperCase();
      const rawSender = m.senderType as any;
      if (sender === 'TOOL' || rawSender === 4) return false;
      if (sender === 'EVENT' || rawSender === 5) return false;
      if (m.toolCallId && (!m.content || m.content.trim() === '')) return false;
      if (!m.content || m.content.trim() === '') return false;
      return true;
    });
  }, [messages]);

  // Helper to extract a day key (YYYY-MM-DD) from a message
  const getMessageDayKey = (msg: MessageItem): string => {
    const rawTime =
      msg.createdAtUtc ||
      msg.createdAt ||
      msg.timestamp ||
      (msg as any).created_at;
    if (!rawTime) return '';
    const date = new Date(rawTime);
    if (isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Scroll detection
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollBottom(!isNearBottom);
    isUserScrollingRef.current = !isNearBottom;
  };

  const scrollToBottom = (smooth = true) => {
    if (bottomAnchorRef.current) {
      bottomAnchorRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end',
      });
    }
  };

  // Auto-scroll on new messages if user isn't actively scrolled up
  useEffect(() => {
    if (!isUserScrollingRef.current) {
      scrollToBottom(false);
    }
  }, [visibleMessages.length]);

  return (
    <div className={cn('relative flex-1 min-h-0 bg-background/50 flex flex-col', className)}>
      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-1"
      >
        {isLoading && (
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-2 max-w-[60%]">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-10 w-full rounded-2xl" />
              </div>
            </div>
            <div className="flex items-start gap-2 max-w-[60%] ml-auto justify-end">
              <div className="space-y-1.5 flex-1 items-end flex flex-col">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-12 w-full rounded-2xl" />
              </div>
            </div>
            <div className="flex items-start gap-2 max-w-[60%]">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-14 w-full rounded-2xl" />
              </div>
            </div>
          </div>
        )}

        {!isLoading && isError && (
          <div className="py-12 text-center">
            <EmptyState
              icon={AlertCircle}
              title="Falha ao carregar o histórico de mensagens"
              description="Não foi possível carregar as mensagens desta conversa."
              actionLabel="Tentar Novamente"
              onAction={() => refetch()}
            />
          </div>
        )}

        {!isLoading && !isError && visibleMessages.length === 0 && (
          <div className="flex-1 flex items-center justify-center py-16">
            <EmptyState
              icon={MessageSquare}
              title="Nenhuma mensagem ainda"
              description="Envie a primeira mensagem abaixo para iniciar a conversa com o cliente."
            />
          </div>
        )}

        {!isLoading &&
          !isError &&
          visibleMessages.length > 0 &&
          visibleMessages.map((message, index) => {
            const currentDay = getMessageDayKey(message);
            const prevDay = index > 0 ? getMessageDayKey(visibleMessages[index - 1]) : null;
            const showDateDivider = Boolean(currentDay && currentDay !== prevDay);

            const rawTime =
              message.createdAtUtc ||
              message.createdAt ||
              message.timestamp ||
              (message as any).created_at;

            return (
              <React.Fragment key={message.id}>
                {showDateDivider && (
                  <div className="flex justify-center my-3 sticky top-1 z-10 select-none">
                    <span className="bg-muted/80 backdrop-blur-xs text-muted-foreground text-[11px] font-medium px-3 py-1 rounded-full border border-border/50 shadow-xs tracking-wide">
                      {formatDayDivider(rawTime)}
                    </span>
                  </div>
                )}
                <MessageBubble
                  message={message}
                  customerName={customerName}
                />
              </React.Fragment>
            );
          })}

        {!isLoading && !isError && isClosed && (
          <div className="flex justify-center my-4">
            <div className="bg-muted/70 text-muted-foreground text-[11px] font-medium px-3.5 py-1.5 rounded-full border border-border/50 text-center max-w-[90%] shadow-xs flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span>
                Conversa encerrada e resolvida{closedAtUtc && !isNaN(new Date(closedAtUtc).getTime()) ? ` em ${new Date(closedAtUtc).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
              </span>
            </div>
          </div>
        )}

        <div ref={bottomAnchorRef} className="h-1" />
      </div>

      {/* Floating Scroll to Bottom Button */}
      {showScrollBottom && (
        <div className="absolute bottom-4 right-6 z-20">
          <Button
            size="icon"
            variant="secondary"
            onClick={() => scrollToBottom(true)}
            className="h-8 w-8 rounded-full shadow-md bg-card/90 hover:bg-card border border-border"
            aria-label="Rolar para o final"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default MessageList;
